import { motion, useAnimation } from 'framer-motion';
import { LineChart as LucideLineChart, TrendingUp, TrendingDown, DollarSign, Users, ShoppingCart, Activity, Sparkles } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Legend,
} from 'recharts';
import { useEffect, useState } from 'react';

// Enhanced data generation with more realistic patterns
const generateRevenueData = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  let baseValue = 4000;
  let trend = 1.1; // Positive trend
  
  return months.map(month => {
    const randomFactor = 1 + (Math.random() * 0.4 - 0.2);
    baseValue = baseValue * trend * randomFactor;
    trend *= 1.02; // Increasing trend over time
    
    return {
      month,
      value: Math.round(baseValue),
      projected: Math.round(baseValue * 1.15 * (1 + Math.random() * 0.1))
    };
  });
};

const generateCustomerData = () => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  let baseValue = 1200;
  let churnRate = 0.08; // Initial churn rate
  
  return months.map(month => {
    const randomGrowth = 1 + (Math.random() * 0.3 - 0.1);
    baseValue = baseValue * randomGrowth;
    churnRate = Math.max(0.05, churnRate * 0.95); // Decreasing churn rate over time
    
    return {
      month,
      value: Math.round(baseValue),
      churn: Math.round(baseValue * churnRate)
    };
  });
};

const marketShareData = [
  { name: 'Your Company', value: 45 },
  { name: 'Competitor A', value: 30 },
  { name: 'Competitor B', value: 15 },
  { name: 'Others', value: 10 },
];

const COLORS = ['#FFB800', '#FF8A00', '#FF5C00', '#FF2E00'];

interface MetricCardProps {
  title: string;
  value: string;
  trend: 'up' | 'down';
  icon: any;
  trendValue: string;
}

const MetricCard = ({ title, value, trend, icon: Icon, trendValue }: MetricCardProps) => {
  const controls = useAnimation();

  useEffect(() => {
    controls.start({
      scale: [1, 1.02, 1],
      transition: { duration: 0.5 }
    });
  }, [value]);

  return (
    <motion.div
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className="bg-gradient-to-b from-amber-400/5 to-transparent p-6 rounded-xl border border-amber-400/10 backdrop-blur-sm hover:shadow-lg hover:shadow-amber-400/10 transition-all duration-300"
    >
      <motion.div animate={controls} className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-gray-400 text-sm">{title}</p>
            {trend === 'up' && (
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles className="w-3 h-3 text-amber-400" />
              </motion.div>
            )}
          </div>
          <h3 className="text-2xl font-bold text-white mt-1">{value}</h3>
        </div>
        <div className="w-10 h-10 bg-amber-400/10 rounded-lg flex items-center justify-center">
          <Icon className="w-5 h-5 text-amber-400" />
        </div>
      </motion.div>
      <div className="flex items-center mt-4">
        {trend === 'up' ? (
          <TrendingUp className="w-4 h-4 text-green-400 mr-1" />
        ) : (
          <TrendingDown className="w-4 h-4 text-red-400 mr-1" />
        )}
        <span className={`text-sm ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
          {trendValue}
        </span>
      </div>
    </motion.div>
  );
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut"
    }
  }
};

export const CommandCenter = () => {
  const [revenueData, setRevenueData] = useState(generateRevenueData());
  const [customerData, setCustomerData] = useState(generateCustomerData());
  const [isHovered, setIsHovered] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  // Simulate real-time updates with smooth transitions
  useEffect(() => {
    const interval = setInterval(() => {
      setRevenueData(prev => {
        const newData = generateRevenueData();
        return newData.map((item, i) => ({
          ...item,
          value: prev[i] ? prev[i].value + (item.value - prev[i].value) * 0.2 : item.value
        }));
      });
      
      setCustomerData(prev => {
        const newData = generateCustomerData();
        return newData.map((item, i) => ({
          ...item,
          value: prev[i] ? prev[i].value + (item.value - prev[i].value) * 0.2 : item.value
        }));
      });
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const chartConfig = {
    tooltip: {
      contentStyle: {
        backgroundColor: '#1E293B',
        border: '1px solid #475569',
        borderRadius: '0.5rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      }
    },
    grid: {
      stroke: "#334155",
      strokeDasharray: "3 3"
    }
  };

  return (
    <section className="relative py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
                AI Command Center
              </span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Real-time insights and predictive analytics powered by advanced AI
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <MetricCard
              title="Revenue"
              value={`$${(revenueData[revenueData.length - 1].value).toLocaleString()}`}
              trend="up"
              icon={DollarSign}
              trendValue="+12.5% vs last month"
            />
            <MetricCard
              title="Active Users"
              value={customerData[customerData.length - 1].value.toLocaleString()}
              trend="up"
              icon={Users}
              trendValue="+23.2% vs last month"
            />
            <MetricCard
              title="Conversion Rate"
              value="3.2%"
              trend="down"
              icon={ShoppingCart}
              trendValue="-0.8% vs last month"
            />
            <MetricCard
              title="Growth Rate"
              value="15.7%"
              trend="up"
              icon={Activity}
              trendValue="+5.3% vs last month"
            />
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-b from-amber-400/5 to-transparent p-6 rounded-xl border border-amber-400/10 backdrop-blur-sm hover:shadow-lg hover:shadow-amber-400/10 transition-all duration-300"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                Revenue Trends
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles className="w-4 h-4 text-amber-400" />
                </motion.div>
              </h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#FFB800" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#FFB800" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorProjected" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid {...chartConfig.grid} />
                    <XAxis dataKey="month" stroke="#94A3B8" />
                    <YAxis stroke="#94A3B8" />
                    <Tooltip {...chartConfig.tooltip} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="value"
                      name="Actual Revenue"
                      stroke="#FFB800"
                      fillOpacity={1}
                      fill="url(#colorRevenue)"
                    />
                    <Area
                      type="monotone"
                      dataKey="projected"
                      name="Projected Revenue"
                      stroke="#60A5FA"
                      fillOpacity={1}
                      fill="url(#colorProjected)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-b from-amber-400/5 to-transparent p-6 rounded-xl border border-amber-400/10 backdrop-blur-sm hover:shadow-lg hover:shadow-amber-400/10 transition-all duration-300"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Customer Growth</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={customerData}>
                    <CartesianGrid {...chartConfig.grid} />
                    <XAxis dataKey="month" stroke="#94A3B8" />
                    <YAxis stroke="#94A3B8" />
                    <Tooltip {...chartConfig.tooltip} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name="Active Users"
                      stroke="#FFB800"
                      strokeWidth={2}
                      dot={{ fill: '#FFB800', strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="churn"
                      name="Churn"
                      stroke="#EF4444"
                      strokeWidth={2}
                      dot={{ fill: '#EF4444', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-b from-amber-400/5 to-transparent p-6 rounded-xl border border-amber-400/10 backdrop-blur-sm hover:shadow-lg hover:shadow-amber-400/10 transition-all duration-300"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
              onHoverStart={() => setIsHovered(true)}
              onHoverEnd={() => setIsHovered(false)}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Market Share</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={marketShareData}
                      cx="50%"
                      cy="50%"
                      innerRadius={isHovered ? 70 : 60}
                      outerRadius={isHovered ? 110 : 100}
                      fill="#8884d8"
                      paddingAngle={5}
                      dataKey="value"
                      animationDuration={300}
                      onMouseEnter={(_, index) => setActiveIndex(index)}
                    >
                      {marketShareData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={COLORS[index % COLORS.length]}
                          opacity={activeIndex === index ? 1 : 0.7}
                        />
                      ))}
                    </Pie>
                    <Tooltip {...chartConfig.tooltip} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="bg-gradient-to-b from-amber-400/5 to-transparent p-6 rounded-xl border border-amber-400/10 backdrop-blur-sm hover:shadow-lg hover:shadow-amber-400/10 transition-all duration-300"
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
            >
              <h3 className="text-xl font-semibold text-white mb-4">Performance Metrics</h3>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData}>
                    <CartesianGrid {...chartConfig.grid} />
                    <XAxis dataKey="month" stroke="#94A3B8" />
                    <YAxis stroke="#94A3B8" />
                    <Tooltip {...chartConfig.tooltip} />
                    <Legend />
                    <Bar 
                      dataKey="value" 
                      name="Revenue"
                      fill="#FFB800" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};